import {
  EpisodicContainerRemovalKind,
  type EpisodicEventItemFragment,
  type EpisodicManifestExemptionState,
  type ShippingDocumentKind,
} from "generated-graphql/graphql";

import type {
  EventManifestExemptions,
  ExemptManifest,
  ExemptStream,
} from "./manifestExemptionForm";

type ContainerRecord = EpisodicEventItemFragment["containerHistory"][number];

/**
 * What one waste stream on one manifest puts in the Manifested column: its
 * containers' shipped weight, capped at what the manifest carries for the
 * stream, or pounds entered by hand in its place.
 */
export type StreamManifested = {
  manifestTrackingNumber: string;
  documentKind: ShippingDocumentKind;
  shippedDate: string | null;
  wasteStreamId: string;
  wasteStreamName: string;
  state: EpisodicManifestExemptionState;
  /** What the manifest carries for the stream. */
  groupPounds: number;
  /** The known shipped weight of the stream's containers on this manifest. */
  containerLb: number;
  containerIds: string[];
  manifestedLb: number;
} & ({ kind: "containers" } | { kind: "capped" } | { kind: "entered"; enteredLb: number });

/** A stream whose Manifested value stands in for its containers' own pounds. */
export type StreamOverride = Exclude<StreamManifested, { kind: "containers" }>;

export type ContainerManifestLink = {
  manifestTrackingNumber: string;
  documentKind: ShippingDocumentKind;
  shippedDate: string | null;
  pounds: number | null;
  /** The stream value that replaces this link's container weight, when the row cannot span it. */
  override: StreamOverride | null;
};

/**
 * The manifests page searched for one link's tracking number, in the link's ship year: the page
 * defaults to the current year, which a URL `shippedDate:` key overrides. Null without a tenant.
 */
export function manifestSearchPath(
  tenantId: string | undefined,
  link: Pick<ContainerManifestLink, "manifestTrackingNumber" | "shippedDate">
): string | null {
  if (!tenantId) {
    return null;
  }
  const year = link.shippedDate?.slice(0, 4);
  // ast-grep-ignore: no-raw-manifest-tracking-number-render
  const withYear = `${link.manifestTrackingNumber} shippedDate:${year}`;
  const omnisearch = year ? withYear : link.manifestTrackingNumber;
  return `/o/${tenantId}/waste/manifests?omnisearch=${encodeURIComponent(omnisearch)}`;
}

/**
 * The containers table's Manifested cell. A stream value covering containers
 * that each shipped only on its manifest spans their rows; a container on
 * several manifests cannot share a span, so it shows the stream value inline
 * on the link it replaces. A shipped container the server also counts as
 * unmatched is awaiting its manifest.
 */
export type ManifestedCell =
  | { kind: "shipped"; links: ContainerManifestLink[] }
  | { kind: "awaiting" }
  | { kind: "stream"; stream: StreamOverride; rowSpan: number }
  | { kind: "coveredBySpan" };

export type EventContainerRow =
  | {
      kind: "container";
      key: string;
      record: ContainerRecord;
      manifests: ContainerManifestLink[];
      manifested: ManifestedCell;
    }
  | {
      /** A stream with entered pounds and none of the event's containers on its manifest. */
      kind: "declaredStream";
      key: string;
      wasteStreamName: string;
      manifests: ContainerManifestLink[];
      manifested: Extract<ManifestedCell, { kind: "stream" }>;
    };

export type ContainerEventSummary = {
  generatedLb: number;
  unweighedCount: number;
  estimatedLb: number | null;
  /** The Manifested column's total, each stream on each manifest counted once. */
  manifestedLb: number;
  /** Containers with a manifest link that carries no weight, each counted once. */
  manifestedUnweighedCount: number;
  manifestCount: number;
  enteredStreamCount: number;
  /** The server's count of shipped containers no manifest line matches yet. */
  awaitingManifestCount: number;
};

const sumKnown = (values: (number | null | undefined)[]) =>
  values.reduce<number>((total, value) => total + (value ?? 0), 0);

const addPounds = (a: number | null, b: number | null) =>
  a === null && b === null ? null : (a ?? 0) + (b ?? 0);

function streamManifested(manifest: ExemptManifest, stream: ExemptStream): StreamManifested {
  const containers = stream.calculated?.containers ?? [];
  const containerLb = sumKnown(containers.map(({ pounds }) => pounds));
  const { groupPounds } = stream;
  const base = {
    manifestTrackingNumber: manifest.manifestTrackingNumber,
    documentKind: manifest.documentKind,
    shippedDate: manifest.shippedDate ?? null,
    wasteStreamId: stream.wasteStream.id,
    wasteStreamName: stream.wasteStream.name,
    state: stream.state,
    groupPounds,
    containerLb,
    containerIds: containers.map(({ container }) => container.id),
  };
  if (stream.declaration) {
    const enteredLb = stream.declaration.declaredPounds;
    return { ...base, kind: "entered", enteredLb, manifestedLb: Math.min(enteredLb, groupPounds) };
  }
  if (containerLb > groupPounds) {
    return { ...base, kind: "capped", manifestedLb: groupPounds };
  }
  return { ...base, kind: "containers", manifestedLb: containerLb };
}

/** Every stream the table shows: entered ones always, others through a container on the record. */
function shownStreams(
  history: ContainerRecord[],
  exemptions: EventManifestExemptions | null
): StreamManifested[] {
  const inHistory = new Set(history.map((record) => record.container.id));
  return (exemptions?.manifests ?? [])
    .flatMap((manifest) => manifest.streams.map((stream) => streamManifested(manifest, stream)))
    .filter(
      (stream) => stream.kind === "entered" || stream.containerIds.some((id) => inHistory.has(id))
    );
}

function linksByContainer(
  exemptions: EventManifestExemptions | null
): Map<string, ContainerManifestLink[]> {
  const links = new Map<string, ContainerManifestLink[]>();
  for (const manifest of exemptions?.manifests ?? []) {
    for (const stream of manifest.streams) {
      for (const { container, pounds } of stream.calculated?.containers ?? []) {
        const forContainer = links.get(container.id) ?? [];
        const existing = forContainer.find(
          (link) => link.manifestTrackingNumber === manifest.manifestTrackingNumber
        );
        if (existing) {
          existing.pounds = addPounds(existing.pounds, pounds ?? null);
        } else {
          forContainer.push({
            manifestTrackingNumber: manifest.manifestTrackingNumber,
            documentKind: manifest.documentKind,
            shippedDate: manifest.shippedDate ?? null,
            pounds: pounds ?? null,
            override: null,
          });
        }
        links.set(container.id, forContainer);
      }
    }
  }
  return links;
}

const isOverride = (stream: StreamManifested): stream is StreamOverride =>
  stream.kind !== "containers";

function overrideFor(
  overrides: StreamOverride[],
  containerId: string,
  manifestTrackingNumber: string
): StreamOverride | null {
  return (
    overrides.find(
      (stream) =>
        stream.manifestTrackingNumber === manifestTrackingNumber &&
        stream.containerIds.includes(containerId)
    ) ?? null
  );
}

/** Containers that can share a span: each shipped only on the stream's manifest, in no other override. */
function spanGroups(
  history: ContainerRecord[],
  overrides: StreamOverride[],
  links: Map<string, ContainerManifestLink[]>
): Map<string, StreamOverride> {
  const inHistory = new Set(history.map((record) => record.container.id));
  const overrideCount = (id: string) => overrides.filter((s) => s.containerIds.includes(id)).length;
  const spanOf = new Map<string, StreamOverride>();
  for (const stream of overrides) {
    const members = stream.containerIds.filter((id) => inHistory.has(id));
    if (members.every((id) => (links.get(id)?.length ?? 0) === 1 && overrideCount(id) === 1)) {
      for (const id of members) {
        spanOf.set(id, stream);
      }
    }
  }
  return spanOf;
}

/**
 * Shipped containers with no manifest link, or none when the list disagrees
 * with the server's count: the server is authoritative, so a mismatch names no one.
 */
function awaitingManifest(
  history: ContainerRecord[],
  exemptions: EventManifestExemptions | null,
  links: Map<string, ContainerManifestLink[]>
): Set<string> {
  if (!exemptions) {
    return new Set();
  }
  const unmatched = history
    .filter(
      (record) =>
        record.lastRemoval === EpisodicContainerRemovalKind.Shipped &&
        !links.has(record.container.id)
    )
    .map((record) => record.container.id);
  return new Set(unmatched.length === exemptions.unmatchedShippedContainerCount ? unmatched : []);
}

/**
 * History order, except a span's rows are pulled up to its first container so
 * they sit together; streams entered without containers follow as rows of their own.
 */
export function eventContainerRows(
  history: ContainerRecord[],
  exemptions: EventManifestExemptions | null
): EventContainerRow[] {
  const links = linksByContainer(exemptions);
  const streams = shownStreams(history, exemptions);
  const overrides = streams.filter(isOverride);
  const spanOf = spanGroups(history, overrides, links);
  const linksOf = (id: string) => links.get(id) ?? [];
  const inHistory = new Set(history.map((record) => record.container.id));
  const awaiting = awaitingManifest(history, exemptions, links);

  const rows: EventContainerRow[] = [];
  const emitted = new Set<string>();
  for (const record of history) {
    const id = record.container.id;
    if (emitted.has(id)) {
      continue;
    }
    const span = spanOf.get(id);
    if (!span) {
      emitted.add(id);
      const shipped = linksOf(id).map((link) => ({
        ...link,
        override: overrideFor(overrides, id, link.manifestTrackingNumber),
      }));
      rows.push({
        kind: "container",
        key: id,
        record,
        manifests: shipped,
        manifested: awaiting.has(id) ? { kind: "awaiting" } : { kind: "shipped", links: shipped },
      });
      continue;
    }
    const members = history.filter((r) => spanOf.get(r.container.id) === span);
    members.forEach((member, index) => {
      emitted.add(member.container.id);
      rows.push({
        kind: "container",
        key: member.container.id,
        record: member,
        manifests: linksOf(member.container.id),
        manifested:
          index === 0
            ? { kind: "stream", stream: span, rowSpan: members.length }
            : { kind: "coveredBySpan" },
      });
    });
  }
  for (const stream of overrides) {
    if (stream.kind === "entered" && !stream.containerIds.some((id) => inHistory.has(id))) {
      rows.push({
        kind: "declaredStream",
        // ast-grep-ignore: no-raw-manifest-tracking-number-render
        key: `${stream.manifestTrackingNumber}:${stream.wasteStreamId}`,
        wasteStreamName: stream.wasteStreamName,
        manifests: [
          {
            manifestTrackingNumber: stream.manifestTrackingNumber,
            documentKind: stream.documentKind,
            shippedDate: stream.shippedDate,
            pounds: null,
            override: stream,
          },
        ],
        manifested: { kind: "stream", stream, rowSpan: 1 },
      });
    }
  }
  return rows;
}

/**
 * Rows `start` up to `end`, each span cut at the page's last row; a span that
 * began on an earlier page heads again on this page's first row.
 */
export function containerRowsPage(
  rows: EventContainerRow[],
  start: number,
  end: number
): EventContainerRow[] {
  const last = Math.min(end, rows.length);
  return rows.slice(start, last).map((row, offset): EventContainerRow => {
    const index = start + offset;
    const cell = row.manifested;
    if (cell.kind === "stream") {
      return { ...row, manifested: { ...cell, rowSpan: Math.min(cell.rowSpan, last - index) } };
    }
    if (cell.kind === "shipped" || cell.kind === "awaiting" || offset > 0) {
      return row;
    }
    let head = index - 1;
    while (head >= 0 && rows[head].manifested.kind === "coveredBySpan") {
      head -= 1;
    }
    const headCell = rows[head]?.manifested;
    if (headCell?.kind !== "stream") {
      return row;
    }
    const remaining = headCell.rowSpan - (index - head);
    return { ...row, manifested: { ...headCell, rowSpan: Math.min(remaining, last - index) } };
  });
}

export function containerEventSummary(
  history: ContainerRecord[],
  exemptions: EventManifestExemptions | null
): ContainerEventSummary {
  const unweighedContainers = new Set(
    eventContainerRows(history, exemptions).flatMap((row) =>
      row.kind === "container" &&
      row.manifested.kind === "shipped" &&
      row.manifested.links.some((link) => link.override === null && link.pounds === null)
        ? [row.record.container.id]
        : []
    )
  );
  const streams = shownStreams(history, exemptions);
  return {
    generatedLb: sumKnown(history.map((record) => record.addedLb)),
    unweighedCount: history.filter((record) => record.addedLb == null).length,
    estimatedLb: exemptions?.epaEstimatedPounds ?? null,
    manifestedLb: sumKnown(streams.map((stream) => stream.manifestedLb)),
    manifestedUnweighedCount: unweighedContainers.size,
    manifestCount: exemptions?.manifests.length ?? 0,
    enteredStreamCount: streams.filter((stream) => stream.kind === "entered").length,
    awaitingManifestCount: exemptions?.unmatchedShippedContainerCount ?? 0,
  };
}
